/**
 * Super Admin 설정 페이지 - 발행처(Issuer) 정보 관리 + 비밀번호 변경
 */

import { useState, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Building2, Lock, Eye, EyeOff } from 'lucide-react';
import { AdminFire, getAdminFunc } from '@/services/SuperAdminFirebase';

interface IssuerInfo {
    name: string;
    registrationNumber: string;  // 適格請求書発行事業者登録番号
    address: string;
    phone: string;
    email: string;
    bankInfo: string;  // 振込先情報
}

const DEFAULT_ISSUER: IssuerInfo = {
    name: 'Sunbi Soft',
    registrationNumber: 'T0000000000000',
    address: '東京都',
    phone: '',
    email: 'billing@sunbisoft.com',
    bankInfo: '',
};

export default function SuperAdminSettingsPage() {
    const { i18n } = useLingui();
    const [issuer, setIssuer] = useState<IssuerInfo>(DEFAULT_ISSUER);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // 비밀번호 변경 상태
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const store = AdminFire.store;
            const docRef = doc(store, 'admin', 'settings');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.issuer) {
                    setIssuer({ ...DEFAULT_ISSUER, ...data.issuer });
                }
            }
        } catch (e) {
            console.error('Failed to load settings:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const store = AdminFire.store;
            const docRef = doc(store, 'admin', 'settings');
            await setDoc(docRef, { issuer }, { merge: true });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save settings:', e);
            alert(t(i18n)`Failed to save settings`);
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess(false);

        // 유효성 검사
        if (!currentPassword) {
            setPasswordError(t(i18n)`Please enter your current password`);
            return;
        }
        if (!newPassword) {
            setPasswordError(t(i18n)`Please enter a new password`);
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError(t(i18n)`Password must be at least 8 characters`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError(t(i18n)`Passwords do not match`);
            return;
        }

        setChangingPassword(true);
        try {
            const changePassword = getAdminFunc('changeSuperAdminPassword');
            await changePassword({
                currentPassword,
                newPassword,
            });
            setPasswordSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordSuccess(false), 3000);
        } catch (e: any) {
            console.error('Failed to change password:', e);
            setPasswordError(e.message || t(i18n)`Failed to change password`);
        } finally {
            setChangingPassword(false);
        }
    };

    const updateIssuer = (field: keyof IssuerInfo, value: string) => {
        setIssuer(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 max-w-4xl space-y-6">
            <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-8 w-8" />
                <div>
                    <h1 className="text-2xl font-bold"><Trans>Settings</Trans></h1>
                    <p className="text-muted-foreground"><Trans>Invoice Issuer Info & Security Settings</Trans></p>
                </div>
            </div>

            {/* 발행처 정보 카드 */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>Issuer Info</Trans></CardTitle>
                    <CardDescription>
                        <Trans>Set the issuer info for invoices & receipts. Please also register the qualified invoice issuer registration number.</Trans>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="issuer-name"><Trans>Company Name</Trans></Label>
                            <Input
                                id="issuer-name"
                                value={issuer.name}
                                onChange={(e) => updateIssuer('name', e.target.value)}
                                placeholder="株式会社○○"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-registration"><Trans>Registration No.</Trans></Label>
                            <Input
                                id="issuer-registration"
                                value={issuer.registrationNumber}
                                onChange={(e) => updateIssuer('registrationNumber', e.target.value)}
                                placeholder="T0000000000000"
                            />
                            <p className="text-xs text-muted-foreground">
                                <Trans>Qualified Invoice Issuer Registration No.</Trans>
                            </p>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="issuer-address"><Trans>Address</Trans></Label>
                            <Input
                                id="issuer-address"
                                value={issuer.address}
                                onChange={(e) => updateIssuer('address', e.target.value)}
                                placeholder="東京都渋谷区..."
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-phone"><Trans>Phone Number</Trans></Label>
                            <Input
                                id="issuer-phone"
                                value={issuer.phone}
                                onChange={(e) => updateIssuer('phone', e.target.value)}
                                placeholder="03-1234-5678"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="issuer-email"><Trans>Email Address</Trans></Label>
                            <Input
                                id="issuer-email"
                                type="email"
                                value={issuer.email}
                                onChange={(e) => updateIssuer('email', e.target.value)}
                                placeholder="billing@example.com"
                            />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="issuer-bank"><Trans>Bank Transfer Info</Trans></Label>
                            <Textarea
                                id="issuer-bank"
                                value={issuer.bankInfo}
                                onChange={(e) => updateIssuer('bankInfo', e.target.value)}
                                placeholder="○○銀行 ○○支店 普通 1234567 カ）○○○○"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <span className="animate-spin mr-2">⏳</span>
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            {saved ? <Trans>Saved</Trans> : <Trans>Save</Trans>}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* 비밀번호 변경 카드 */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        <Trans>Change Password</Trans>
                    </CardTitle>
                    <CardDescription>
                        <Trans>Change the Super Admin account password.</Trans>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="current-password"><Trans>Current Password</Trans></Label>
                            <div className="relative">
                                <Input
                                    id="current-password"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="new-password"><Trans>New Password</Trans></Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                <Trans>8 or more characters</Trans>
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm-password"><Trans>Confirm Password</Trans></Label>
                            <Input
                                id="confirm-password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    {passwordError && (
                        <p className="text-sm text-red-500">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                        <p className="text-sm text-green-600"><Trans>Password changed successfully</Trans></p>
                    )}

                    <div className="flex justify-end pt-4 border-t">
                        <Button
                            onClick={handleChangePassword}
                            disabled={changingPassword}
                            variant="outline"
                        >
                            {changingPassword ? (
                                <span className="animate-spin mr-2">⏳</span>
                            ) : (
                                <Lock className="mr-2 h-4 w-4" />
                            )}
                            <Trans>Change Password</Trans>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
